import PropTypes from 'prop-types';
import cn from 'classnames';

import {
  validateUsage,
  altValue,
  isAriaHidden
} from './image.helpers.jsx';

import { LdsImageError } from './LdsImageError.jsx';

export default function LdsImage({
  alt,
  decorative = false,
  id,
  images,
  loading,
  responsive = false,
  src,
  className,
  ...rest
}) {
  // If the critical prop useage or values are invalid handle it
  if (!validateUsage(src, responsive, images)) return <LdsImageError />;

  // If the valid image uses responsive mode, generate a responsive picture image
  if (responsive) {
    // Give the fallback image an HTML ID if the component has an ID (which renders on the picture element).
    let fallbackImgId;
    if (id) fallbackImgId = `${id}-fallback`;

    // Render it
    return (
      <picture
        id={id}
        className={cn(
          'lds-image',
          className
        )}
        aria-hidden={isAriaHidden(decorative)}
        {...rest}
      >
        { images && Array.isArray(images) && images.map((image, i) => (
          <source
            key={i}
            media={image.media}
            srcSet={image.srcset}
          />
        ))}
        <img
          id={fallbackImgId}
          src={images[images.length - 1].srcset}
          alt={altValue(alt, decorative)}
          aria-hidden={isAriaHidden(decorative)}
          loading={loading}
        />
      </picture>
    );
  }

  // Otherwise assum the valid image is a basic HTML image
  // Render it
  return (
    <img
      id={id}
      className={cn(
        'lds-image',
        className
      )}
      src={src}
      alt={altValue(alt, decorative)}
      aria-hidden={isAriaHidden(decorative)}
      loading={loading}
      {...rest}
    />
  );
}

LdsImage.propTypes = {
  alt: PropTypes.string,
  decorative: PropTypes.bool,
  loading: PropTypes.oneOf(['eager', 'lazy', undefined]),
  src: PropTypes.string,
  responsive: PropTypes.bool,
  images: PropTypes.arrayOf((propValue, key) => { // eslint-disable-line
    if (!propValue[key].srcset) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsImage: responsive image object missing 'srcset' property at index ${key} (${key + 1}/${propValue.length}).
      Each responsive image object must contain an 'srcset' property.
      `);
    }
    if (!propValue[key].media && key !== (propValue.length - 1)) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsImage: responsive image object missing 'media' property at index ${key} (${key + 1}/${propValue.length}).
      Each responsive image object EXCEPT the fallback default must contain a 'media' property.
      `);
    }
    if (propValue[key].media && key === (propValue.length - 1)) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsImage: responsive image default fallback object contains an invalid 'media' property at index ${key} (${key + 1}/${propValue.length}).
      The fallback responsive image object MUST always be the last object in the array and MUST NOT contain a 'media' property.
      `);
    }
  }),
};
